import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Cryptocurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { EmptyState } from '@sorare/core/src/components/EmptyState';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useCryptoAmount } from '@sorare/core/src/hooks/useCryptoAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { svgCoord } from '@sorare/core/src/lib/svg';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  TransactionType,
  transactionTypeMessages,
} from '#src/lib/transactions.ts';

const GraphContainer = styled.div`
  --graph-padding-bottom: calc(var(--unit) * 5);
  padding-bottom: var(--graph-padding-bottom);
  min-height: 460px;
  max-height: 500px;
  width: 100%;
  position: relative;
  &.small {
    display: flex;
    --graph-padding-bottom: 0;
    min-height: 108px;
    justify-content: center;
    &.withLegend {
      min-height: calc(108px + calc(var(--unit) * 3));
    }
  }
  &.withLegend {
    --graph-padding-bottom: calc(var(--unit) * 3);
  }
`;

const StyledEmptyState = styled(EmptyState)`
  .small & {
    padding: var(--unit);
    img {
      width: calc(7 * var(--unit));
      height: calc(6.5 * var(--unit));
    }
  }
`;

const BoldLabelM = styled(LabelM).attrs({ bold: true })``;

const StyledSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

// Styled components for the points and tooltips outside the SVG
const PointsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const DataPoint = styled(Horizontal).attrs({ gap: 0, center: true })`
  --data-point-unit: 6px;
  --shape-border-width: 1px;
  position: absolute;
  transform: translate(-50%, -50%);
  cursor: pointer;
  width: var(--data-point-unit);
  height: var(--data-point-unit);

  @media ${tabletAndAbove} {
    --data-point-unit: 10px;
    --shape-border-width: 2px;
    .small & {
      --data-point-unit: 6px;
      --shape-border-width: 1px;
    }
  }
`;

const ColouredShape = styled.span`
  width: var(--data-point-unit);
  height: var(--data-point-unit);
  box-shadow: 0 0 2px var(--c-black);

  border-radius: 50%;

  &.${TransactionType.PRIMARY_OFFER} {
    border-radius: 0;
    border: var(--shape-border-width) solid var(--c-onBase-success);
  }
  &.${TransactionType.AUCTION} {
    background-color: var(--c-onBase-success);
  }
  &.${TransactionType.PUBLIC_OFFER} {
    border-radius: 0;
    background: var(--c-yellow-300);
    transform: rotate(45deg);
  }
  &.${TransactionType.DIRECT_OFFER} {
    border-radius: 0;
    transform: rotate(45deg);
    border: var(--shape-border-width) solid var(--c-yellow-300);
  }
  &.buyer {
    &.${TransactionType.PRIMARY_OFFER}, &.${TransactionType.DIRECT_OFFER} {
      border-color: var(--c-green-400);
    }
    &.${TransactionType.PUBLIC_OFFER}, &.${TransactionType.AUCTION} {
      background-color: var(--c-green-400);
    }
  }
  &.seller {
    &.${TransactionType.PRIMARY_OFFER}, &.${TransactionType.DIRECT_OFFER} {
      border-color: var(--c-blue-400);
    }
    &.${TransactionType.PUBLIC_OFFER}, &.${TransactionType.AUCTION} {
      background-color: var(--c-red-400);
    }
  }
`;

const Legend = styled(Horizontal)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: var(--quadruple-unit);
  justify-content: center;
  flex-wrap: wrap;
  .small & {
    height: var(--triple-unit);
  }
`;

const LegendItem = styled(LabelM)`
  --data-point-unit: 10px;
  --shape-border-width: 2px;
  display: flex;
  align-items: center;
  gap: var(--unit);
  .small & {
    font: var(--t-label-s);
  }
  &.disabled {
    opacity: 0.5;
  }
`;

type DataPointType = {
  x: string;
  y: number;
  tooltipContent: NonNullable<ReactNode>;
  type: TransactionType;
  isBuyer?: boolean;
  isSeller?: boolean;
};

type GraphDataPoint = {
  x: number;
  y: number;
  tooltipContent: NonNullable<ReactNode>;
  type: TransactionType;
  // Add pixel positions for DOM elements
  pxX: number;
  pxY: number;
  isBuyer?: boolean;
  isSeller?: boolean;
};

type Props = {
  currency: Currency;
  cryptocurrency: Cryptocurrency;
  from: Date;
  to: Date;
  data: DataPointType[];
  loading: boolean;
  small?: boolean;
  withLegend?: boolean;
  transactionTypes?: TransactionType[];
};

export const DumbGraph = ({
  currency,
  cryptocurrency,
  from,
  to,
  data,
  loading,
  small = false,
  withLegend = false,
  transactionTypes,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const smallOrMobile = small || !isTabletAndAbove;
  const { formatNumber, formatDistanceToNowStrict, formatMessage } =
    useIntlContext();
  const { formatCryptoMajor } = useCryptoAmount(cryptocurrency);

  // Create a ref for the container and state to keep track of its dimensions.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(small ? 313 : 700);
  const [containerHeight, setContainerHeight] = useState(small ? 108 : 350);

  useEffect(() => {
    if (!containerRef.current) return () => {};
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      setContainerHeight(entry.contentRect.height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const {
    leftPadding,
    margin,
    viewBoxWidth,
    viewBoxHeight,
    chartWidth,
    chartHeight,
    minDate,
    dateRange,
    minY,
    amplitude,
    lines,
    columns,
    commonCryptoDecimals,
    points,
    pathD,
    areaPathD,
    timeTicks,
  } = useMemo(() => {
    // Define constants for layout
    /* eslint-disable @typescript-eslint/no-shadow */
    const leftPadding = smallOrMobile ? 15 : 20;
    const leftBreathing = smallOrMobile ? 0 : 10; // extra left space for first point
    const margin = {
      left: leftPadding,
      right: smallOrMobile ? 20 : 10,
      top: 10,
      bottom: small ? 4 : 25,
    };

    // Compute viewBox dimensions based on container size and left padding
    const viewBoxWidth = containerWidth;
    const viewBoxHeight = containerHeight;
    const chartWidth =
      viewBoxWidth - margin.left - margin.right - leftBreathing;
    const chartHeight = viewBoxHeight - margin.top - margin.bottom;

    const minDate = data.length > 0 ? from.getTime() : 0;
    const maxDate = data.length > 0 ? to.getTime() : 0;
    const dateRange = maxDate - minDate;

    // Compute y scale based on data values with a safety guard for zero-range.
    const dataMin = 0;
    const dataMax = Math.max(...data.map(({ y }) => y));
    const valueRange = dataMax - dataMin;

    const scale =
      valueRange === 0 ? 1 : 10 ** Math.floor(Math.log10(valueRange));
    const minY = Math.floor(dataMin / scale) * scale;
    const maxY = Math.ceil(dataMax / scale) * scale;
    const amplitude = maxY - minY;

    // Compute grid lines: steps of "scale" from 0 to amplitude
    const numGridLines = amplitude / scale + 1;
    const lines = Array.from({ length: numGridLines }).map(
      (_, index) => index * scale
    );

    // Compute common Ethereum decimals (avoid division by zero)
    const decimalsCandidates = lines.map((value, index, array) => {
      if (index === array.length - 1) return 0;
      const diff = Math.abs(value - array[index + 1]) || 1; // fallback diff = 1
      return Math.floor(-Math.log10(diff));
    });
    const filteredDecimals = decimalsCandidates.filter(num => Boolean(num));
    const commonCryptoDecimals =
      filteredDecimals.length > 0 ? Math.max(...filteredDecimals) : 0;

    // Transform data points to chart coordinates
    const points: GraphDataPoint[] = data.map(item => {
      const time = new Date(item.x).getTime();
      const xVal = dateRange === 0 ? 0.5 : (time - minDate) / dateRange;
      // Map the timestamp to x coordinate with extra left breathing room.
      const x =
        margin.left +
        xVal * (viewBoxWidth - margin.right - (margin.left + leftBreathing));
      const yVal = amplitude === 0 ? 0.5 : (item.y - minY) / amplitude;
      const y = margin.top + (1 - yVal) * chartHeight; // Invert y-axis so higher values appear at the top

      // Calculate pixel positions for DOM elements
      // Convert SVG coordinates to pixel positions
      const pxX = ((x + leftPadding) * containerWidth) / viewBoxWidth;
      const pxY = (y * containerHeight) / viewBoxHeight;

      return { ...item, x, y, pxX, pxY };
    });

    // Build the SVG path for the curve using a cubic Bézier for each segment.
    let pathD = '';
    if (points.length > 0) {
      pathD = `M ${svgCoord(points[0].x)} ${svgCoord(points[0].y)}`;
      for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const dx = (p1.x - p0.x) / 2;
        pathD += ` C ${svgCoord(p0.x + dx)} ${svgCoord(p0.y)}, ${svgCoord(p1.x - dx)} ${svgCoord(p1.y)}, ${svgCoord(p1.x)} ${svgCoord(p1.y)}`;
      }
    }

    // Compute an area path that closes the shape under the curve.
    let areaPathD = '';
    if (points.length > 0) {
      areaPathD = `${pathD} L ${svgCoord(points[points.length - 1].x)} ${svgCoord(
        margin.top + chartHeight
      )} L ${svgCoord(points[0].x)} ${svgCoord(margin.top + chartHeight)} Z`;
    }

    // Compute relative time ticks for the legend.
    const numTimeTicks = containerWidth < 500 ? 3 : 4;
    const timeTicks = Array.from({ length: numTimeTicks }).map((_, i) =>
      dateRange === 0 ? minDate : minDate + (dateRange * i) / (numTimeTicks - 1)
    );
    const columns = timeTicks.map(tick => {
      const x =
        margin.left +
        (dateRange === 0
          ? chartWidth / 2
          : ((tick - minDate) / dateRange) * chartWidth);
      return {
        x,
        y: margin.top + chartHeight - 1,
      };
    });

    return {
      leftPadding,
      margin,
      viewBoxWidth,
      viewBoxHeight,
      chartWidth,
      chartHeight,
      minDate,
      dateRange,
      minY,
      amplitude,
      lines,
      columns,
      commonCryptoDecimals,
      points,
      pathD,
      areaPathD,
      timeTicks,
    };
    /* eslint-enable @typescript-eslint/no-shadow */
  }, [smallOrMobile, small, containerWidth, containerHeight, data, from, to]);

  const formatGridLabel = (value: number) => {
    if (currency === Currency.FIAT) {
      return formatNumber(value, {
        maximumSignificantDigits: 4,
        notation: 'compact',
        compactDisplay: 'short',
      });
    }
    return formatCryptoMajor(
      value,
      {
        notation: 'compact',
        compactDisplay: 'short',
        minimumFractionDigits: commonCryptoDecimals,
        maximumFractionDigits: commonCryptoDecimals,
      },
      { hideSymbol: true }
    );
  };

  if (data.length === 0 && !loading) {
    return (
      <GraphContainer
        ref={containerRef}
        className={classNames({ small, withLegend })}
      >
        <StyledEmptyState
          title={
            <ConditionalWrapper wrap={small} Wrapper={BoldLabelM}>
              <FormattedMessage defaultMessage="No data" />
            </ConditionalWrapper>
          }
        />
      </GraphContainer>
    );
  }
  const displayedLines = small
    ? [lines[0], lines[Math.floor(lines.length / 2)], lines[lines.length - 1]]
    : lines;

  return (
    <GraphContainer
      ref={containerRef}
      className={classNames({ small, withLegend })}
    >
      <Skeleton loading={loading}>
        {loading ? (
          <div style={{ width: viewBoxWidth, height: chartHeight }} />
        ) : (
          <StyledSVG
            viewBox={`-${leftPadding} 0 ${viewBoxWidth} ${viewBoxHeight}`}
            preserveAspectRatio="xMinYMin meet"
          >
            <defs>
              <linearGradient
                id="blueGradient"
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1={`${margin.top}`}
                x2="0"
                y2={`${margin.top + chartHeight}`}
              >
                <stop
                  offset="0%"
                  stopColor="var(--c-blue-400)"
                  stopOpacity="0.25"
                />
                <stop
                  offset="100%"
                  stopColor="var(--c-blue-400)"
                  stopOpacity="0.05"
                />
              </linearGradient>
            </defs>

            {/* Render grid lines and their labels */}
            {displayedLines.map((lineValue, index) => {
              const fraction = lineValue / amplitude;
              const y = margin.top + (1 - fraction) * chartHeight || 0;
              return (
                // oxlint-disable-next-line react/no-array-index-key
                <g key={index}>
                  <line
                    x1={margin.left}
                    x2={margin.left + chartWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--c-nd-100)"
                    strokeWidth={smallOrMobile ? 1 : 2}
                  />
                  <text
                    x={margin.left - 5}
                    y={y}
                    textAnchor="end"
                    alignmentBaseline="middle"
                    fill="var(--c-nd-600)"
                    fontSize={smallOrMobile ? '10' : '12'}
                  >
                    {formatGridLabel(minY + lineValue)}
                  </text>
                </g>
              );
            })}
            {!small &&
              columns.map(({ x, y }) => (
                <g key={`${x}-${y}`}>
                  <line
                    x1={x}
                    x2={x}
                    y1={margin.top}
                    y2={y}
                    stroke="var(--c-nd-100)"
                    strokeWidth={smallOrMobile ? 1 : 2}
                  />
                </g>
              ))}

            {/* Render the blue gradient area under the curve */}
            {!!areaPathD && (
              <path d={areaPathD} fill="url(#blueGradient)" stroke="none" />
            )}

            {/* Render the curved line connecting the data points */}
            <path
              d={pathD}
              fill="none"
              stroke="var(--c-blue-400)"
              strokeWidth={smallOrMobile ? 1 : 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Render time legend at the bottom with relative timestamps */}
            {!small && (
              <g className="time-legend">
                {timeTicks.map((tick, i) => {
                  const nowLessThanOnHour =
                    // eslint-disable-next-line react-hooks/purity
                    new Date(tick).getTime() > Date.now() - 1000 * 60 * 60;
                  const xPosRaw =
                    margin.left +
                    (dateRange === 0
                      ? chartWidth / 2
                      : ((tick - minDate) / dateRange) * chartWidth);
                  const textMargin = nowLessThanOnHour ? 20 : 40; // safe padding from the chart edges
                  const xPos = Math.max(
                    margin.left + textMargin,
                    Math.min(xPosRaw, margin.left + chartWidth - textMargin)
                  );

                  return (
                    <text
                      // oxlint-disable-next-line react/no-array-index-key
                      key={i}
                      x={xPos}
                      y={margin.top + chartHeight + 15} // legend Y position offset
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize={smallOrMobile ? '10' : '12'}
                      fill="var(--c-nd-600)"
                    >
                      {nowLessThanOnHour
                        ? formatMessage(glossary.now)
                        : formatDistanceToNowStrict(tick, {
                            addSuffix: true,
                          })}
                    </text>
                  );
                })}
              </g>
            )}
          </StyledSVG>
        )}
        {/* Render points and tooltips outside the SVG */}
        <PointsContainer>
          {points.map(point => (
            <Tooltip
              key={`point-${point.type}-${point.x}-${point.y}`}
              title={point.tooltipContent}
              style={{
                position: 'absolute',
                left: `${point.pxX}px`,
                top: `${point.pxY}px`,
                ...((point.isBuyer || point.isSeller) && { zIndex: 1 }),
              }}
            >
              <DataPoint>
                <ColouredShape
                  className={classNames(point.type, {
                    buyer: point.isBuyer,
                    seller: point.isSeller,
                  })}
                />
              </DataPoint>
            </Tooltip>
          ))}
        </PointsContainer>
      </Skeleton>
      {!!withLegend && (
        <Legend center gap={isTabletAndAbove ? 3 : 1.5}>
          {Object.values(TransactionType).map(t => (
            <LegendItem
              key={t}
              color="var(--c-nd-600)"
              className={classNames({
                disabled: !transactionTypes?.includes(t),
              })}
            >
              <ColouredShape className={t} />
              <FormattedMessage {...transactionTypeMessages[t]} />
            </LegendItem>
          ))}
        </Legend>
      )}
    </GraphContainer>
  );
};
